import React from 'react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BillingConnectionData } from '../../../services/billing-integration/response/BillingConnectionResponse';
import { formatDateTime, formatRelative, scopeLabel } from '../utils/format';

interface BillingConnectionTableProps {
    connections: BillingConnectionData[];
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    onEdit: (connection: BillingConnectionData) => void;
    onDelete: (connection: BillingConnectionData) => void;
    deletingId: string | null;
}

const COLUMN_COUNT = 6;

const BillingConnectionTable: React.FC<BillingConnectionTableProps> = ({
    connections,
    isLoading,
    isError,
    onRetry,
    onEdit,
    onDelete,
    deletingId,
}) => (
    <div className="table-responsive">
        <table className="table table-striped align-middle mb-0">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th>Last pulled</th>
                    <th className="text-end">Actions</th>
                </tr>
            </thead>
            <tbody>
                {isLoading && (
                    <tr>
                        <td colSpan={COLUMN_COUNT} className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Loading connections...
                        </td>
                    </tr>
                )}
                {!isLoading && isError && (
                    <tr>
                        <td colSpan={COLUMN_COUNT} className="text-center text-danger py-4">
                            Failed to load billing connections.{' '}
                            <button type="button" className="btn btn-link p-0 align-baseline" onClick={onRetry}>Try again</button>
                        </td>
                    </tr>
                )}
                {!isLoading && !isError && connections.length === 0 && (
                    <tr>
                        <td colSpan={COLUMN_COUNT} className="text-center text-muted py-4">
                            No billing connections yet. Click <strong>Add connection</strong> and paste the Code and Key from the Billing app.
                        </td>
                    </tr>
                )}
                {!isLoading && !isError && connections.map((connection) => (
                    <tr key={connection.id}>
                        <td>{connection.name || <span className="text-muted">Untitled</span>}</td>
                        <td className="font-monospace">{connection.code}</td>
                        <td>
                            <div>{connection.scope_name || '-'}</div>
                            <div className="small text-muted">{scopeLabel(connection.scope_type)}</div>
                            {connection.property_inactive && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>This property has been deactivated, so Billing sees it as removed. Consider deleting this connection.</Tooltip>}
                                >
                                    <span className="badge bg-danger text-white mt-1" style={{ cursor: 'help' }}>Property inactive</span>
                                </OverlayTrigger>
                            )}
                        </td>
                        <td>
                            {connection.is_active
                                ? <span className="badge bg-success text-white">Active</span>
                                : <span className="badge bg-secondary text-white">Inactive</span>}
                        </td>
                        <td>
                            <div>{formatDateTime(connection.last_pulled_at)}</div>
                            {connection.last_pulled_at && (
                                <div className="small text-muted">{formatRelative(connection.last_pulled_at)}</div>
                            )}
                        </td>
                        <td className="text-end">
                            <Dropdown align="end">
                                <Dropdown.Toggle variant="default" id={`billing-connection-actions-${connection.id}`}>
                                    Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                    <Dropdown.Item onClick={() => onEdit(connection)}>Edit</Dropdown.Item>
                                    <Dropdown.Item
                                        className="text-danger"
                                        onClick={() => onDelete(connection)}
                                        disabled={deletingId === connection.id}
                                    >
                                        {deletingId === connection.id ? 'Deleting...' : 'Delete'}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export default BillingConnectionTable;
